// src/components/Modals/AssetImportModal.js
import React, { useState } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Container,
  Paper,
  Slide, // <-- MOVED HERE
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { assetImportService } from "../../services/assetImportService";

// Slide Transition matching the CreateEditAssetModal
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AssetImportModal = ({ open, onClose, currentUser, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const blob = await assetImportService.generateTemplateBlob(currentUser);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Niyantrix_Asset_Import_Template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setResult({
        success: false,
        message: err.message || "Failed to download template",
        errors: [],
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setResult(null);
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await assetImportService.importFromFile(file, currentUser);
      setResult(res);
      if (res.success) {
        onSuccess?.(res);
      }
    } catch (err) {
      setResult({
        success: false,
        message: err.message || "Unexpected error",
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadErrorLog = () => {
    if (!result?.errors?.length) return;
    const lines = [
      "Niyantrix Asset Import — Validation Log",
      `File: ${file?.name || "unknown"}`,
      `Date: ${new Date().toISOString()}`,
      `Failed rows: ${result.errors.length}`,
      "",
      ...result.errors.flatMap((e) =>
        e.messages.map((m) => `Row ${e.row}: ${m}`)
      ),
    ];
    const blob = new Blob([lines.join("\n")], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `asset-import-errors-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    if (loading) return;
    setFile(null);
    setResult(null);
    onClose();
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!loading ? handleClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          width: "100vw",
          height: "100vh",
          maxWidth: "100vw",
          maxHeight: "100vh",
          margin: 0,
          boxSizing: "border-box",
        },
      }}
    >
      {/* APP BAR */}
      <AppBar
        sx={{
          position: "relative",
          flexShrink: 0,
          width: "100%",
          boxSizing: "border-box",
        }}
        elevation={1}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }} noWrap>
              Import Assets from Excel
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
              Upload your filled template to bulk-create assets
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* SCROLLABLE FORM BODY */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          width: "100%",
          boxSizing: "border-box",
          bgcolor: "#f1f5f9",
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              border: "1px solid #e2e8f0",
              backgroundColor: "#fff",
            }}
          >
            <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
              Download the official template (dropdowns included). Do not change
              headers or column order. Asset IDs and metadata are generated
              automatically. All rows are validated first — if any row fails,
              nothing is imported.
            </Alert>

            {/* Upload Controls Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
                mb: 4,
                alignItems: "stretch",
              }}
            >
              {/* Download Template Box */}
              <Box
                sx={{
                  flex: 1,
                  p: 3,
                  border: "1px dashed #cbd5e1",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  backgroundColor: "#f8fafc",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Step 1: Get the Template
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Download the Excel template pre-filled with your company's
                  master data.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={
                    downloading ? <CircularProgress size={16} /> : <DownloadIcon />
                  }
                  onClick={handleDownloadTemplate}
                  disabled={downloading || loading}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                >
                  Download Template
                </Button>
              </Box>

              {/* Upload File Box */}
              <Box
                sx={{
                  flex: 1,
                  p: 3,
                  border: "1px dashed #cbd5e1",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  backgroundColor: "#f8fafc",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Step 2: Upload Filled File
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select the `.xlsx` file you've populated with asset data.
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={loading}
                  sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
            </Box>

            {/* Selected File Info */}
            {file && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 2,
                  mb: 3,
                  bgcolor: "#eff6ff",
                  borderRadius: 2,
                  border: "1px solid #bfdbfe",
                }}
              >
                <InsertDriveFileIcon color="primary" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
                {!loading && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setFile(null)}
                    sx={{ textTransform: "none" }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            )}

            {loading && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

            {/* Results Section */}
            {result && (
              <Box sx={{ mt: 2 }}>
                <Alert
                  severity={result.success ? "success" : "error"}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  {result.message}
                </Alert>

                {result?.errors?.length > 0 && (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        Validation Errors ({result.errors.length} rows failed)
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={downloadErrorLog}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Download Error Log
                      </Button>
                    </Box>
                    <Paper
                      variant="outlined"
                      sx={{ maxHeight: 400, overflow: "auto", borderRadius: 2 }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, width: 80 }}>
                              Row
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Errors</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.errors.map((e) => (
                            <TableRow key={e.row}>
                              <TableCell sx={{ verticalAlign: "top", fontWeight: 600 }}>
                                {e.row}
                              </TableCell>
                              <TableCell>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                  {e.messages.map((m, i) => (
                                    <li key={i} style={{ color: "#dc2626" }}>
                                      {m}
                                    </li>
                                  ))}
                                </ul>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      {/* STICKY FOOTER */}
      <Box
        sx={{
          flexShrink: 0,
          width: "100%",
          height: "72px",
          maxHeight: "72px",
          boxSizing: "border-box",
          borderTop: "1px solid #e2e8f0",
          backgroundColor: "#fff",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          px: 3,
        }}
      >
        <Button
          type="button"
          onClick={handleClose}
          disabled={loading}
          sx={{
            px: 3,
            py: 1,
            backgroundColor: "#f3f4f6",
            color: "#374151",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            fontWeight: 500,
            textTransform: "none",
            minWidth: "100px",
            height: "40px",
            "&:hover": { backgroundColor: "#e5e7eb" },
            "&:disabled": { opacity: 0.6 },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            px: 3,
            py: 1,
            backgroundColor: "#2563eb",
            color: "#fff",
            borderRadius: "6px",
            fontWeight: 600,
            textTransform: "none",
            minWidth: "160px",
            height: "40px",
            "&:hover": { backgroundColor: "#1d4ed8" },
            "&:disabled": { backgroundColor: "#93c5fd" },
          }}
        >
          {loading ? "Validating & Importing…" : "Import Data"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AssetImportModal;